<script setup>
import { cardTitleBg } from "../utils/ui.js";
import { reactive, ref, nextTick } from "vue";
import AppIcon from "./AppIcon.vue";

const emit = defineEmits([
  "edit-card",
  "node-out-down",
  "drop-target",
  "title-down",
  "title-up",
  "link-add",
  "delete-card",
  "set-story",
  "redraw-lines",
]);

const props = defineProps({
  cardData: {
    type: Object,
    required: true,
  },
  editData: {
    type: Object,
    required: true,
  },
  cardId: {
    type: String,
    required: true,
  },
  storyData: {
    type: Object,
    required: true,
  },
  currentCardEditing: {
    type: String,
    required: false,
  },
  selectedCards: {
    type: Array,
    default: () => [],
  },
});

const conditionOperators = {
  lt: "<",
  gt: ">",
  lte: "<=",
  gte: ">=",
  eq: "==",
  ne: "!=",
  eval: "eval",
};

function showOutputNode(nodeType) {
  if (nodeType == "detour" || nodeType == "operator" || nodeType == "meta")
    return true;
  return false;
}

function cardDescription(cardData) {
  if (cardData.nodeType == "detour") return "Storylet";
  if (cardData.nodeType == "start") return "Input";
  if (cardData.nodeType == "end") return "Output";
  if (cardData.nodeType == "card") return "Content";
  if (cardData.nodeType == "note") return "Note";
  return cardData.description;
}

let deleteConfirm = reactive({
  waiting: false,
});

function deleteCard(cardId) {
  if (deleteConfirm.waiting) {
    emit("delete-card", cardId);
    deleteConfirm.waiting = false;
  } else {
    deleteConfirm.waiting = true;
  }
}

function deleteCancel() {
  deleteConfirm.waiting = false;
}

function currentCardCss() {
  let css = "";
  if (props.cardId == props.currentCardEditing) {
    css += "edit-current";
  }
  if (props.selectedCards?.includes(props.cardId)) {
    css += (css ? " " : "") + "card-selected";
  }
  return css;
}

function firstConditionText(link) {
  if (link.conditions.length > 0) {
    const first = link.conditions[0];

    if (first.variable === "__show_once__") {
      return "Show once";
    }

    const operatorSymbol = conditionOperators[first.operator];
    const hasValue = first.value !== "" && first.value !== undefined && first.value !== null;

    if (!first.variable || !operatorSymbol || !hasValue) {
      return "Unset";
    }

    return first.variable + " " + operatorSymbol + " " + first.value;
  }

  return "...";
}

function setStory(story) {
  emit("set-story", story);
}

const showParagraphs = ref(false);

async function toggleParagraphs() {
  showParagraphs.value = !showParagraphs.value;
  await nextTick();
  emit("redraw-lines");
}
</script>

<template>
  <div :class="'card noselect ' + cardData.tags + ' ' + currentCardCss()" :style="{
    left: cardData.editor.left + 'px',
    top: cardData.editor.top + 'px',
    zIndex: cardData.editor.top,
  }">
    <h3 :style="{
      backgroundColor: cardTitleBg(cardData.nodeType),
    }" @mousedown="$emit('title-down', cardId)" @mouseup="$emit('title-up')">
      {{ cardDescription(cardData) }}
    </h3>

    <div v-if="cardData.nodeType == 'start'" class="top-left">
      <div v-bind:key="linkindex" v-for="(link, linkindex) in cardData.links">
        <div class="linkcircle" :id="'link_' + cardId + '_' + linkindex" @mousedown="
          $emit('node-out-down', [
            'link_' + cardId + '_' + linkindex,
            cardId,
            linkindex,
          ])
          "></div>
      </div>
    </div>

    <div v-if="cardData.nodeType == 'card'">
      <div v-if="cardData.paragraphs?.length > 0" class="para-preview">
        <div class="para-preview-header" @click.stop="toggleParagraphs" @mousedown.stop>
          <span>{{ cardData.paragraphs.length }} paragraph{{ cardData.paragraphs.length !== 1 ? 's' : '' }}</span>
          <AppIcon :name="showParagraphs ? 'chevron-up' : 'chevron-down'" />
        </div>
        <div v-if="showParagraphs" class="para-preview-body">
          <p v-for="para in cardData.paragraphs" :key="para.text">
            {{ storyData.loc[para.text]?.[storyData.currentLanguage] }}
          </p>
        </div>
      </div>
      <div class="linkincard" v-bind:key="linkindex" v-for="(link, linkindex) in cardData.links">
        <div v-if="link.linkText" class="crop">
          {{ storyData.loc[link.linkText][storyData.currentLanguage] }}
        </div>
        <div v-if="link.linkText" class="linkcircle" :id="'link_' + cardId + '_' + linkindex" @mousedown="
          $emit('node-out-down', [
            'link_' + cardId + '_' + linkindex,
            cardId,
            linkindex,
          ])
          "></div>
      </div>
    </div>

    <div v-if="cardData.nodeType == 'detour'" class="hide-x pad-s">
      <select v-model="cardData.detours[0].target" class="ui-select">
        <option v-bind:key="storyindex" v-for="(story, storyindex) in storyData.stories" :value="storyindex">
          {{ storyData.loc[story.info.name]["en"] }}
        </option>
      </select>
    </div>

    <div v-if="cardData.nodeType == 'logic'">
      <div class="linkincard" v-bind:key="linkindex" v-for="(link, linkindex) in cardData.links">
        <div v-if="link.linkText" class="crop">
          {{ firstConditionText(link) }}
        </div>
        <div v-if="link.linkText" class="linkcircle" :id="'link_' + cardId + '_' + linkindex" @mousedown="
          $emit('node-out-down', [
            'link_' + cardId + '_' + linkindex,
            cardId,
            linkindex,
          ])
          "></div>
      </div>
    </div>

    <div v-if="cardData.nodeType == 'note'" class="note-body">
      {{ cardData.text }}
    </div>

    <div v-if="cardData.nodeType == 'meta'">
      <div class="linkincard" v-bind:key="idx" v-for="(item, idx) in cardData.custom">
        <div class="crop">{{ item.customKey }}: {{ item.customValue }}</div>
      </div>
    </div>

    <div v-if="cardData.nodeType != 'start' && cardData.nodeType != 'note'" class="cardinputcircle"
      @mouseover="$emit('drop-target', cardId)" @mouseout="$emit('drop-target', 'none')" @mouseup="$emit('title-up')">
    </div>

    <div class="cardoutputcircle linkcircle" v-if="showOutputNode(cardData.nodeType)" :id="'link_' + cardId + '_0'"
      @mousedown="$emit('node-out-down', ['link_' + cardId + '_0', cardId, 0])"></div>

    <div class="quick-link" @mousedown.stop>
      <span v-if="cardData.nodeType != 'start' && cardData.nodeType != 'end'"
        :title="deleteConfirm.waiting ? 'Confirm delete' : 'Delete card'" @click="deleteCard(cardId)"
        @mouseleave="deleteCancel()" :style="{
          backgroundColor: deleteConfirm.waiting ? 'red' : '',
        }">
        <AppIcon :name="deleteConfirm.waiting ? 'question' : 'trash'" />
      </span>

      <span v-if="
        cardData.nodeType == 'card' ||
        cardData.nodeType == 'operator' ||
        cardData.nodeType == 'meta' ||
        cardData.nodeType == 'logic' ||
        cardData.nodeType == 'note'
      " title="Edit card" @mouseup="$emit('title-up')" @click="$emit('edit-card', cardId)">
        <AppIcon name="pen-to-square" />
      </span>

      <span v-if="cardData.nodeType == 'detour'" title="Edit storylet" @mouseup="$emit('title-up')"
        @click="setStory(cardData.detours[0].target)">
        <AppIcon name="pen-to-square" />
      </span>

      <span title="Add link" @click="$emit('link-add', cardId)"
        v-if="cardData.nodeType == 'card' || cardData.nodeType == 'logic'">
        <AppIcon name="plus" />
      </span>
    </div>
  </div>
</template>
